import { z } from 'zod';

// Email validation schema
export const emailSchema = z?.string()?.min(1, 'Email is required')?.email('Please enter a valid email address')?.max(255, 'Email is too long');

// Password validation schema
export const passwordSchema = z?.string()?.min(8, 'Password must be at least 8 characters')?.max(128, 'Password is too long')?.regex(/[A-Z]/, 'Password must contain at least one uppercase letter')?.regex(/[a-z]/, 'Password must contain at least one lowercase letter')?.regex(/[0-9]/, 'Password must contain at least one number');

// Sign in form validation
export const signInSchema = z?.object({
  email: emailSchema,
  password: z?.string()?.min(1, 'Password is required')
});

// Symptom entry validation
export const symptomEntrySchema = z?.object({
  feelingLevel: z?.string()?.min(1, 'Please select how you\'re feeling today (good, mild, moderate, or severe)')?.refine(
      (val) => ['good', 'mild', 'moderate', 'severe']?.includes(val),
      { message: 'Please select a valid feeling level: good, mild, moderate, or severe' }
    ),
  symptomCategories: z?.array(z?.string())?.optional(),
  bodyLocations: z?.array(z?.string())?.optional(),
  severity: z?.number()?.min(1)?.max(10)?.optional(),
  timing: z?.string()?.optional(),
  triggers: z?.array(z?.string())?.optional(),
  dailyImpacts: z?.array(z?.string())?.optional(),
  medicationStatus: z?.string()?.optional(),
  notes: z?.string()?.max(5000, 'Notes are too long')?.optional(),
  voiceNote: z?.string()?.optional()
});

// User profile validation
export const userProfileSchema = z?.object({
  full_name: z?.string()?.min(1, 'Name is required')?.max(255, 'Name is too long')?.optional(),
  user_type: z?.enum(['child', 'elderly', 'chronic', 'general'])?.optional(),
  date_of_birth: z?.string()?.optional(),
  emergency_contact: z?.string()?.max(255)?.optional()
});

// Email recipient validation
export const emailRecipientSchema = z?.string()?.email('Please enter a valid email address')?.refine(
    (email) => {
      // Prevent email injection attacks
      const dangerousChars = /[\r\n\0]/;
      return !dangerousChars?.test(email);
    },
    { message: 'Invalid email format' }
  );

// Validate data against schema
export const validate = (schema, data) => {
  try {
    const result = schema?.parse(data);
    return { success: true, data: result, errors: null };
  } catch (error) {
    if (error instanceof z?.ZodError) {
      const formattedErrors = {};
      error?.errors?.forEach((err) => {
        const path = err?.path?.join('.');
        formattedErrors[path] = err?.message;
      });
      return { success: false, data: null, errors: formattedErrors };
    }
    return { success: false, data: null, errors: { _general: 'Validation failed' } };
  }
};

export default {
  emailSchema,
  passwordSchema,
  signInSchema,
  symptomEntrySchema,
  userProfileSchema,
  emailRecipientSchema,
  validate
};